// Browser-only host. Captures audio (screen share) on this device and
// broadcasts it as 20 ms interleaved-stereo PCM frames over a WebRTC
// DataChannel — one per joiner.
//
// We deliberately don't use WebRTC's MediaConnection here because
// MediaStream transport gives every receiver its own opaque jitter
// buffer and hides per-frame timestamps, which means peers drift apart
// by tens or hundreds of milliseconds. The whole point of this product
// is "phones playing the same note at the same time," so we keep the
// LAN path's anchor-chain + NTP-min sync and just swap WS for
// DataChannel. See p2pSession.ts for the joiner counterpart.

import { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { buildJoinUrl, displayCode, PeerHub, type PeerEvent } from './PeerHub'
import { HostStreamer } from './hostStreamer'

type Status = 'idle' | 'opening' | 'ready' | 'error'

function formatTime(sec: number): string {
  if (isNaN(sec) || !isFinite(sec)) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s < 10 ? '0' : ''}${s}`
}

export function BrowserHost({ onExit }: { onExit: () => void }) {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [myId, setMyId] = useState<string | null>(null)
  const [peers, setPeers] = useState<string[]>([])
  const [streamLabel, setStreamLabel] = useState<string | null>(null)
  const [shareError, setShareError] = useState<string | null>(null)
  const [qrSvg, setQrSvg] = useState<string | null>(null)
  
  // Audio source settings
  const [sourceType, setSourceType] = useState<'screen' | 'file'>('screen')
  const [file, setFile] = useState<File | null>(null)
  const [playlist, setPlaylist] = useState<File[]>([])
  const [playlistIndex, setPlaylistIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hostVolume, setHostVolume] = useState(0.8)
  const [hostMuted, setHostMuted] = useState(false)
  const [localPlaybackEnabled, setLocalPlaybackEnabled] = useState(true)

  const playlistIndexRef = useRef(playlistIndex)
  playlistIndexRef.current = playlistIndex
  const playlistRef = useRef(playlist)
  playlistRef.current = playlist

  const hubRef = useRef<PeerHub | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const streamerRef = useRef<HostStreamer | null>(null)
  const audioRef = useRef<HTMLMediaElement | null>(null)
  const videoContainerRef = useRef<HTMLDivElement | null>(null)

  const onTimeUpdate = () => {
    if (audioRef.current) {
      const cur = audioRef.current.currentTime
      setCurrentTime(cur)
      if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
        try {
          navigator.mediaSession.setPositionState({
            duration: audioRef.current.duration || 0,
            playbackRate: 1,
            position: cur,
          })
        } catch { /* noop */ }
      }
    }
  }
  const onDurationChange = () => {
    if (audioRef.current) setDuration(audioRef.current.duration || 0)
  }
  const onEnded = () => {
    const currentPlaylist = playlistRef.current
    const currentIdx = playlistIndexRef.current
    if (currentPlaylist.length > 0 && currentIdx + 1 < currentPlaylist.length) {
      playTrackIndex(currentIdx + 1)
    } else {
      setIsPlaying(false)
      setCurrentTime(0)
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none'
      }
    }
  }
  const onPlay = () => {
    setIsPlaying(true)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing'
    }
  }
  const onPause = () => {
    setIsPlaying(false)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'paused'
    }
  }

  const cleanupMediaSession = () => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null
      navigator.mediaSession.playbackState = 'none'
      try {
        navigator.mediaSession.setActionHandler('play', null)
        navigator.mediaSession.setActionHandler('pause', null)
        navigator.mediaSession.setActionHandler('stop', null)
        navigator.mediaSession.setActionHandler('seekto', null)
      } catch { /* noop */ }
    }
  }

  const cleanupAudioElement = () => {
    cleanupMediaSession()
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeEventListener('timeupdate', onTimeUpdate)
      audioRef.current.removeEventListener('durationchange', onDurationChange)
      audioRef.current.removeEventListener('ended', onEnded)
      audioRef.current.removeEventListener('play', onPlay)
      audioRef.current.removeEventListener('pause', onPause)
      if (audioRef.current.src) {
        try {
          URL.revokeObjectURL(audioRef.current.src)
        } catch { /* noop */ }
      }
      audioRef.current = null
    }
    if (videoContainerRef.current) {
      videoContainerRef.current.innerHTML = ''
    }
  }

  // Boot the PeerJS host once.
  useEffect(() => {
    const hub = new PeerHub()
    hubRef.current = hub
    const onPeer = (e: Event) => {
      const ev = (e as CustomEvent<PeerEvent>).detail
      switch (ev.type) {
        case 'open':
          setMyId(ev.id)
          setStatus('ready')
          break
        case 'connected':
          setPeers((prev) => Array.from(new Set([...prev, ev.peerId])))
          break
        case 'disconnected':
          setPeers((prev) => prev.filter((p) => p !== ev.peerId))
          break
        case 'error':
          setError(ev.message)
          setStatus('error')
          break
      }
    }
    hub.addEventListener('peer', onPeer)
    setStatus('opening')
    hub.startAsHost().catch((e) => {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('error')
    })
    return () => {
      hub.removeEventListener('peer', onPeer)
      streamerRef.current?.stop()
      streamerRef.current = null
      hub.destroy()
      hubRef.current = null
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop()
        streamRef.current = null
      }
      cleanupAudioElement()
    }
  }, [])

  // Render the join URL into a dark-mode SVG QR when our ID is ready.
  const joinUrl = useMemo(() => (myId ? buildJoinUrl(myId) : ''), [myId])
  useEffect(() => {
    if (!joinUrl) return
    let cancelled = false
    QRCode.toString(joinUrl, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 240,
      color: { dark: '#fafafa', light: '#0b0b0c' },
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg)
      })
      .catch(() => setQrSvg(null))
    return () => {
      cancelled = true
    }
  }, [joinUrl])

  const isVideo = !!file?.type.startsWith('video/')

  const playNext = () => {
    const currentPlaylist = playlistRef.current
    const currentIdx = playlistIndexRef.current
    if (currentPlaylist.length > 0 && currentIdx + 1 < currentPlaylist.length) {
      playTrackIndex(currentIdx + 1)
    }
  }

  const playPrevious = () => {
    const currentPlaylist = playlistRef.current
    const currentIdx = playlistIndexRef.current
    if (currentPlaylist.length > 0 && currentIdx > 0) {
      playTrackIndex(currentIdx - 1)
    }
  }

  const playTrackIndex = (index: number) => {
    const currentPlaylist = playlistRef.current
    if (currentPlaylist.length === 0) return
    const nextFile = currentPlaylist[index]
    setPlaylistIndex(index)
    setFile(nextFile)
    void startFileStream(nextFile, index)
  }

  const readEntry = async (entry: any): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file((file: File) => resolve([file]))
      })
    } else if (entry.isDirectory) {
      const reader = entry.createReader()
      const readEntries = (): Promise<any[]> => {
        return new Promise((resolve, reject) => {
          reader.readEntries(resolve, reject)
        })
      }
      const allEntries = []
      let entries = await readEntries()
      while (entries.length > 0) {
        allEntries.push(...entries)
        entries = await readEntries()
      }
      const filesPromises = allEntries.map(e => readEntry(e))
      const filesArrays = await Promise.all(filesPromises)
      return filesArrays.flat()
    }
    return []
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const items = Array.from(e.dataTransfer.items || [])
    if (items.length === 0) return

    stopStreaming()
    cleanupAudioElement()

    if (!items[0] || !items[0].webkitGetAsEntry) {
      const files = Array.from(e.dataTransfer.files || [])
      const mediaFiles = files.filter(f => f.type.startsWith('audio/') || f.type.startsWith('video/'))
      if (mediaFiles.length > 0) {
        mediaFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
        setPlaylist(mediaFiles)
        setPlaylistIndex(0)
        setFile(mediaFiles[0])
        void startFileStream(mediaFiles[0], 0)
      }
      return
    }

    const filesPromises = items.map(item => {
      const entry = item.webkitGetAsEntry()
      return entry ? readEntry(entry) : Promise.resolve([])
    })

    const filesArrays = await Promise.all(filesPromises)
    const allFiles = filesArrays.flat().filter(f => f.type.startsWith('audio/') || f.type.startsWith('video/'))

    if (allFiles.length > 0) {
      allFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
      setPlaylist(allFiles)
      setPlaylistIndex(0)
      setFile(allFiles[0])
      void startFileStream(allFiles[0], 0)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return
    
    stopStreaming()
    cleanupAudioElement()

    const mediaFiles = selectedFiles.filter(f => f.type.startsWith('audio/') || f.type.startsWith('video/'))
    if (mediaFiles.length > 0) {
      mediaFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
      setPlaylist(mediaFiles)
      setPlaylistIndex(0)
      setFile(mediaFiles[0])

      void startFileStream(mediaFiles[0], 0)
    }
  }

  const startShare = async () => {
    setShareError(null)
    if (streamRef.current) return
    // eslint-disable-next-line no-console
    console.info('[Multispeaker host] startShare: requesting getDisplayMedia…')
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 1 },
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 48_000,
          channelCount: 2,
        } as MediaTrackConstraints,
      })
      // eslint-disable-next-line no-console
      console.info(
        `[Multispeaker host] startShare: got stream (audio tracks=${stream.getAudioTracks().length}, video tracks=${stream.getVideoTracks().length})`,
      )
      if (stream.getAudioTracks().length === 0) {
        stream.getTracks().forEach((t) => t.stop())
        throw new Error(
          'Share didn\'t include audio. In the picker, tick "Share audio" or pick a tab playing sound.',
        )
      }
      // Drop video immediately — we only ever needed it to satisfy
      // getDisplayMedia's contract.
      stream.getVideoTracks().forEach((t) => t.stop())
      streamRef.current = stream
      const hub = hubRef.current
      if (!hub) throw new Error('mesh not ready')
      const streamer = new HostStreamer({ stream, hub })
      streamerRef.current = streamer
      // eslint-disable-next-line no-console
      console.info('[Multispeaker host] startShare: calling streamer.start()…')
      await streamer.start()
      // eslint-disable-next-line no-console
      console.info('[Multispeaker host] startShare: streamer.start() returned')
      setStreamLabel('Screen audio (live)')
      // If the user revokes the share via the browser toolbar, tear down.
      const a = stream.getAudioTracks()[0]
      a.addEventListener('ended', stopStreaming)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      // eslint-disable-next-line no-console
      console.error('[Multispeaker host] startShare failed:', e)
      setShareError(msg)
      stopStreaming()
    }
  }

  const setupMediaSession = (audio: HTMLMediaElement, activeFile: File, activeIndex: number) => {
    if (!('mediaSession' in navigator)) return

    navigator.mediaSession.metadata = new MediaMetadata({
      title: activeFile.name,
      artist: 'Multispeaker Host',
      album: `P2P Playlist (${activeIndex + 1}/${playlist.length || 1})`,
    })

    navigator.mediaSession.setActionHandler('play', () => {
      audio.play().catch((e) => console.error('MediaSession play failed:', e))
    })
    navigator.mediaSession.setActionHandler('pause', () => {
      audio.pause()
    })
    navigator.mediaSession.setActionHandler('stop', () => {
      stopStreaming()
    })
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime !== undefined) {
        audio.currentTime = details.seekTime
        setCurrentTime(details.seekTime)
      }
    })

    if (playlist.length > 0 && activeIndex + 1 < playlist.length) {
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        playNext()
      })
    } else {
      navigator.mediaSession.setActionHandler('nexttrack', null)
    }

    if (playlist.length > 0 && activeIndex > 0) {
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        playPrevious()
      })
    } else {
      navigator.mediaSession.setActionHandler('previoustrack', null)
    }
  }

  const startFileStream = async (activeFile?: File, activeIndex?: number) => {
    setShareError(null)
    const fileToPlay = activeFile || file
    const indexToUse = activeIndex !== undefined ? activeIndex : playlistIndex
    if (!fileToPlay) return
    try {
      const hub = hubRef.current
      if (!hub) throw new Error('mesh not ready')

      if (streamerRef.current) {
        streamerRef.current.stop()
      }

      cleanupAudioElement()

      let media: HTMLMediaElement
      const isVideoFile = fileToPlay.type.startsWith('video/')

      if (isVideoFile) {
        const video = document.createElement('video')
        video.className = "w-full max-h-[400px] object-contain"
        video.preload = "auto"
        video.playsInline = true
        video.src = URL.createObjectURL(fileToPlay)
        video.load()

        if (videoContainerRef.current) {
          videoContainerRef.current.innerHTML = ''
          videoContainerRef.current.appendChild(video)
        }

        media = video
      } else {
        const audio = new Audio()
        audio.preload = 'auto'
        audio.src = URL.createObjectURL(fileToPlay)
        audio.load()

        media = audio
      }

      media.addEventListener('timeupdate', onTimeUpdate)
      media.addEventListener('durationchange', onDurationChange)
      media.addEventListener('ended', onEnded)
      media.addEventListener('play', onPlay)
      media.addEventListener('pause', onPause)

      audioRef.current = media

      const streamer = new HostStreamer({
        audioElement: media,
        localPlayback: localPlaybackEnabled,
        hub,
      })
      streamerRef.current = streamer
      if (localPlaybackEnabled) {
        streamer.setLocalVolume(hostVolume)
        streamer.setLocalMuted(hostMuted)
      }

      await streamer.start()
      setStreamLabel(fileToPlay.name)
      setupMediaSession(media, fileToPlay, indexToUse)

      await media.play()
      setIsPlaying(true)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[Multispeaker host] startFileStream failed:', e)
      setShareError(msg)
      stopStreaming()
    }
  }

  const stopStreaming = () => {
    cleanupMediaSession()
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setIsPlaying(false)

    streamerRef.current?.stop()
    streamerRef.current = null

    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) t.stop()
      streamRef.current = null
    }
    setStreamLabel(null)
  }

  const togglePlayPause = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch((e) => {
        console.error('Failed to play audio:', e)
      })
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    if (audioRef.current) {
      audioRef.current.currentTime = val
      setCurrentTime(val)
    }
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    setHostVolume(val)
    if (streamerRef.current) {
      streamerRef.current.setLocalVolume(val)
    }
  }

  const toggleMute = () => {
    const nextMuted = !hostMuted
    setHostMuted(nextMuted)
    if (streamerRef.current) {
      streamerRef.current.setLocalMuted(nextMuted)
    }
  }

  const copyUrl = async () => {
    if (!joinUrl) return
    try {
      await navigator.clipboard.writeText(joinUrl)
    } catch {
      /* clipboard blocked in non-secure contexts */
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 space-y-6">
      <header className="flex items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Multispeaker</h1>
        <button
          type="button"
          onClick={() => {
            stopStreaming()
            onExit()
          }}
          className="text-xs text-zinc-500 underline-offset-2 hover:underline"
        >
          end session
        </button>
      </header>

      <section className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
        <h2 className="text-sm font-medium uppercase tracking-wide text-zinc-400">
          Join code
        </h2>
        {status === 'opening' && (
          <p className="mt-2 text-sm text-zinc-400">Setting up the room…</p>
        )}
        {status === 'error' && (
          <p className="mt-2 text-sm text-red-400">{error}</p>
        )}
        {status === 'ready' && myId && (
          <div className="mt-3 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
            <div className="shrink-0">
              {qrSvg ? (
                <div
                  className="overflow-hidden rounded-lg ring-1 ring-zinc-800"
                  style={{ width: 240, height: 240 }}
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                />
              ) : (
                <div className="h-[240px] w-[240px] rounded-lg bg-zinc-800" />
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Code</div>
              <div className="font-mono text-3xl tracking-[0.3em] text-zinc-100">
                {displayCode(myId)}
              </div>
              <div className="break-all rounded-md bg-zinc-950 px-3 py-2 font-mono text-xs text-zinc-300 ring-1 ring-zinc-800">
                {joinUrl}
              </div>
              <button
                type="button"
                onClick={copyUrl}
                className="rounded-md bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-100 ring-1 ring-zinc-700 transition active:scale-95"
              >
                Copy link
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wide text-zinc-400">Source</h2>
          <div className="flex rounded-lg bg-zinc-950 p-1 ring-1 ring-zinc-800">
            <button
              type="button"
              onClick={() => {
                stopStreaming()
                setSourceType('screen')
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                sourceType === 'screen' ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Screen Share
            </button>
            <button
              type="button"
              onClick={() => {
                stopStreaming()
                setSourceType('file')
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                sourceType === 'file' ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Local File
            </button>
          </div>
        </div>

        {sourceType === 'screen' ? (
          <div>
            <p className="text-xs text-zinc-500">
              Pick a tab or screen and tick <em>Share audio</em>. Anything that plays in
              that tab is streamed to every joiner.
            </p>
            {shareError && (
              <p className="mt-2 text-sm text-red-400 break-words">{shareError}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {streamLabel ? (
                <>
                  <span className="text-sm text-emerald-400">● {streamLabel}</span>
                  <button
                    type="button"
                    onClick={stopStreaming}
                    className="rounded-md bg-red-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm ring-1 ring-red-400 transition active:scale-95"
                  >
                    Stop sharing
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void startShare()}
                  disabled={!window.isSecureContext}
                  className="rounded-md bg-blue-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition active:scale-95 disabled:bg-zinc-700"
                  title={
                    window.isSecureContext
                      ? "Share whatever's playing on your screen"
                      : 'Needs HTTPS — open this page over HTTPS or via localhost'
                  }
                >
                  Share screen audio
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-zinc-500">
              Select local files or drag & drop a folder to stream. This works on mobile devices where screen sharing is not available.
            </p>
            {shareError && (
              <p className="text-sm text-red-400 break-words">{shareError}</p>
            )}
            {!file ? (
              <label
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition ${
                  isDragging
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950/20 hover:bg-zinc-950/40'
                }`}
              >
                <svg className={`w-8 h-8 mb-2 transition-colors ${isDragging ? 'text-blue-500' : 'text-zinc-500'}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-zinc-400 font-medium text-center">
                  {isDragging ? 'Drop folders or files here!' : 'Choose file(s) or drag & drop folder'}
                </span>
                <input
                  type="file"
                  multiple
                  accept="audio/*,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="rounded-xl bg-zinc-950/40 p-4 border border-zinc-800 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate text-zinc-100">{file.name}</div>
                    <div className="text-xs text-zinc-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                      {playlist.length > 1 && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-zinc-850 text-[10px] text-zinc-400 font-semibold">
                          {playlistIndex + 1} / {playlist.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      stopStreaming()
                      setPlaylist([])
                      setPlaylistIndex(0)
                      setFile(null)
                    }}
                    className="text-xs text-zinc-500 hover:text-zinc-300 underline"
                  >
                    Change file
                  </button>
                </div>

                <div
                  ref={videoContainerRef}
                  className={`relative w-full overflow-hidden rounded-xl bg-black border border-zinc-800 flex items-center justify-center min-h-[200px] max-h-[400px] ${
                    file && isVideo ? 'block' : 'hidden'
                  }`}
                />

                {/* Local Playback Toggle */}
                {!streamLabel && (
                  <div className="flex items-center justify-between gap-4 border-t border-zinc-900 pt-3">
                    <div className="flex-1 pr-2">
                      <label htmlFor="play-on-host" className="text-xs font-semibold text-zinc-300 block">Play audio on host device</label>
                      <p className="text-[10px] text-zinc-500 mt-0.5">Uncheck to save CPU and hotspot bandwidth if you only want joiners to play sound.</p>
                    </div>
                    <input
                      id="play-on-host"
                      type="checkbox"
                      checked={localPlaybackEnabled}
                      onChange={(e) => setLocalPlaybackEnabled(e.target.checked)}
                      className="w-4 h-4 rounded bg-zinc-950 border-zinc-800 text-blue-500 focus:ring-blue-500 shrink-0"
                    />
                  </div>
                )}

                {!streamLabel ? (
                  <button
                    type="button"
                    onClick={() => void startFileStream()}
                    className="w-full rounded-md bg-blue-500 py-2 text-sm font-medium text-white shadow-sm active:scale-95 transition"
                  >
                    Start streaming file
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={playPrevious}
                        disabled={playlist.length <= 1 || playlistIndex === 0}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-850 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 active:scale-95 transition disabled:opacity-30 disabled:pointer-events-none"
                        title="Previous file"
                      >
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                          <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={togglePlayPause}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-500 text-white hover:scale-105 active:scale-95 transition shadow-md shrink-0"
                      >
                        {isPlaying ? (
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={playNext}
                        disabled={playlist.length <= 1 || playlistIndex === playlist.length - 1}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-850 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 active:scale-95 transition disabled:opacity-30 disabled:pointer-events-none"
                        title="Next file"
                      >
                        <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                          <path d="M6 18l8.5-6L6 6zm9-12v12h2V6z"/>
                        </svg>
                      </button>
                      <div className="flex-1 space-y-1">
                        <input
                          type="range"
                          min={0}
                          max={duration || 100}
                          value={currentTime}
                          onChange={handleSeek}
                          className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none"
                        />
                        <div className="flex justify-between text-[10px] text-zinc-500 font-mono">
                          <span>{formatTime(currentTime)}</span>
                          <span>{formatTime(duration)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-zinc-900 pt-3 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={toggleMute}
                        className="p-1 hover:bg-zinc-900 rounded transition"
                      >
                        {hostMuted || hostVolume === 0 ? (
                          <svg className="w-4 h-4 fill-current text-zinc-400" viewBox="0 0 24 24">
                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.21.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 fill-current text-zinc-400" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                          </svg>
                        )}
                      </button>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-xs text-zinc-500 font-medium shrink-0">Host Vol:</span>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={hostVolume}
                          onChange={handleVolumeChange}
                          className="flex-1 h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-zinc-500 font-mono w-8 text-right">
                          {Math.round(hostVolume * 100)}%
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={stopStreaming}
                      className="w-full rounded-md bg-red-500/10 hover:bg-red-500/20 py-2 text-xs font-semibold text-red-400 transition"
                    >
                      Stop streaming
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-zinc-400">
          Connected ({peers.length})
        </h2>
        {peers.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Waiting for someone to join. Share the code or QR above.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900/30">
            {peers.map((p) => (
              <li key={p} className="px-4 py-2 font-mono text-xs text-zinc-300">
                {displayCode(p)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
